import React from 'react';
import {
    Box,
    Chip,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    ListSubheader,
    Checkbox,
    ListItemText,
} from '@mui/material';

interface Item {
    id: string;
    name: string;
    category?: {
        id: string;
        name: string;
    };
    italic?: boolean;
}

interface Category {
    id: string;
    name: string;
}

interface GroupedItemSelectorProps {
    label: string;
    items: Item[];
    categories: Category[];
    selectedIds: string[];
    onChange: (ids: string[]) => void;
    helperText?: string;
    disabled?: boolean;
}

export default function GroupedItemSelector({
    label,
    items,
    categories,
    selectedIds,
    onChange,
    helperText = 'Leave empty to include all items',
    disabled = false,
}: GroupedItemSelectorProps) {
    const handleChange = (event: SelectChangeEvent<typeof selectedIds>) => {
        const value = event.target.value;
        onChange(typeof value === 'string' ? value.split(',') : value);
    };

    // Group items by category
    const itemsByCategory = items.reduce((acc: Record<string, Item[]>, item) => {
        const categoryId = item.category?.id || 'uncategorized';
        if (!acc[categoryId]) {
            acc[categoryId] = [];
        }
        acc[categoryId].push(item);
        return acc;
    }, {});

    // Handle category checkbox click
    const handleCategoryClick = (categoryId: string) => {
        const categoryItems = itemsByCategory[categoryId] || [];
        const categoryItemIds = categoryItems.map(item => item.id);

        // Check if all items in this category are already selected
        const allSelected = categoryItemIds.every(id => selectedIds.includes(id));

        if (allSelected) {
            // If all selected, deselect all items in this category
            onChange(selectedIds.filter(id => !categoryItemIds.includes(id)));
        } else {
            // Otherwise, select all items in this category
            const newSelectedIds = [...selectedIds];
            categoryItemIds.forEach(id => {
                if (!newSelectedIds.includes(id)) {
                    newSelectedIds.push(id);
                }
            });
            onChange(newSelectedIds);
        }
    };

    // Determine checkbox state for each category
    const getCategoryCheckboxState = (categoryId: string) => {
        const categoryItems = itemsByCategory[categoryId] || [];
        if (categoryItems.length === 0) return { checked: false, indeterminate: false };

        const categoryItemIds = categoryItems.map(item => item.id);
        const selectedCount = categoryItemIds.filter(id => selectedIds.includes(id)).length;

        return {
            checked: selectedCount > 0,
            indeterminate: selectedCount > 0 && selectedCount < categoryItems.length,
        };
    };

    return (
        <FormControl sx={{ width: '100%', mt: 1 }} disabled={disabled}>
            <InputLabel id={`${label.toLowerCase()}-select-label`}>{label}</InputLabel>
            <Select
                labelId={`${label.toLowerCase()}-select-label`}
                multiple
                value={selectedIds}
                onChange={handleChange}
                input={<OutlinedInput label={label} />}
                renderValue={selected => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map(value => {
                            const item = items.find(i => i.id === value);
                            return <Chip key={value} label={item?.name || value} />;
                        })}
                    </Box>
                )}
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: 300,
                        },
                    },
                }}
            >
                {categories.map(category => {
                    const checkboxState = getCategoryCheckboxState(category.id);
                    return [
                        <ListSubheader
                            key={`header-${category.id}`}
                            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                            onClick={e => {
                                e.stopPropagation();
                                handleCategoryClick(category.id);
                            }}
                        >
                            <Checkbox
                                checked={checkboxState.checked}
                                indeterminate={checkboxState.indeterminate}
                                onClick={e => e.stopPropagation()}
                                onChange={() => handleCategoryClick(category.id)}
                            />
                            <ListItemText primary={category.name} />
                        </ListSubheader>,
                        ...(itemsByCategory[category.id] || []).map(item => (
                            <MenuItem key={item.id} value={item.id} sx={{ pl: 4 }}>
                                <Checkbox checked={selectedIds.includes(item.id)} />
                                <ListItemText
                                    primary={item.name}
                                    primaryTypographyProps={{
                                        fontStyle: item.italic ? 'italic' : 'normal',
                                    }}
                                />
                            </MenuItem>
                        )),
                    ];
                })}

                {/* Handle uncategorized items if any */}
                {itemsByCategory['uncategorized'] &&
                    itemsByCategory['uncategorized'].length > 0 &&
                    (() => {
                        const checkboxState = getCategoryCheckboxState('uncategorized');
                        return [
                            <ListSubheader
                                key="header-uncategorized"
                                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                                onClick={e => {
                                    e.stopPropagation();
                                    handleCategoryClick('uncategorized');
                                }}
                            >
                                <Checkbox
                                    checked={checkboxState.checked}
                                    indeterminate={checkboxState.indeterminate}
                                    onClick={e => e.stopPropagation()}
                                    onChange={() => handleCategoryClick('uncategorized')}
                                />
                                <ListItemText primary="Other" />
                            </ListSubheader>,
                            ...itemsByCategory['uncategorized'].map(item => (
                                <MenuItem key={item.id} value={item.id} sx={{ pl: 4 }}>
                                    <Checkbox checked={selectedIds.includes(item.id)} />
                                    <ListItemText
                                        primary={item.name}
                                        primaryTypographyProps={{
                                            fontStyle: item.italic ? 'italic' : 'normal',
                                        }}
                                    />
                                </MenuItem>
                            )),
                        ];
                    })()}
            </Select>
            <FormHelperText>{helperText}</FormHelperText>
        </FormControl>
    );
}
